// ============================================================================
// JARVISTRAVEL - COACH MARK
// One step of the first-run tour (JAR-263): a spotlight scrim that dims the
// page around the anchored element, plus a small card with title, body, step
// dots, and Skip / Next controls. Because it contains buttons it is a
// NON-MODAL dialog, not role="tooltip" (WAI-ARIA: tooltips must never hold
// interactive content). Escape and scrim-tap both dismiss; dismissal is the
// caller's cue to end the tour permanently.
// ============================================================================

import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface CoachMarkProps {
  /** Element the mark points at. Measured live; re-measured on resize/scroll. */
  anchor: Element | null;
  title: string;
  body: string;
  /** 1-based position in the tour. With totalSteps 1 the step chrome hides. */
  step: number;
  totalSteps: number;
  /** Advance (Next / Done / Got it). */
  onNext: () => void;
  /** Leave the tour (Skip, Escape, scrim tap). */
  onDismiss: () => void;
}

const CARD_WIDTH = 280;
const EDGE = 12; // viewport gutter
const SPOT_PAD = 6; // breathing room around the anchor inside the spotlight

export function CoachMark({ anchor, title, body, step, totalSteps, onNext, onDismiss }: CoachMarkProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [rect, setRect] = useState<DOMRect | null>(() => anchor?.getBoundingClientRect() ?? null);

  // Track the anchor through resize/scroll so the spotlight never drifts.
  // Measured synchronously in a PASSIVE listener: passive means the compositor
  // never waits on us (the real scroll-jank guard), and the unchanged-rect
  // bail below skips the re-render on idle ticks. Deliberately NOT deferred
  // to requestAnimationFrame — rAF starves in throttled/background tabs,
  // freezing the spotlight away from its anchor (observed in live testing).
  useEffect(() => {
    if (!anchor) return;
    const measure = () => {
      const next = anchor.getBoundingClientRect();
      setRect((prev) => {
        if (prev && prev.left === next.left && prev.top === next.top && prev.width === next.width && prev.height === next.height) {
          return prev;
        }
        return next;
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { capture: true, passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, { capture: true });
    };
  }, [anchor]);

  // Glide between steps, snap while tracking: true on the render where the
  // anchor changed (new step), false for same-anchor rect updates (scroll).
  const lastAnchor = useRef<Element | null>(null);
  const glide = lastAnchor.current !== anchor;
  useEffect(() => {
    lastAnchor.current = anchor;
  }, [anchor]);

  // Focus lands on the card for each step so keyboard/SR users follow the tour.
  useEffect(() => {
    cardRef.current?.focus();
  }, [step]);

  // Escape leaves the tour from anywhere, matching Modal's document listener.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const isLast = step >= totalSteps;
  const single = totalSteps <= 1;
  const nextLabel = single ? 'Got it' : isLast ? 'Done' : 'Next';

  // Card below the anchor when it sits in the top half of the viewport,
  // above it otherwise; horizontally centered on the anchor and clamped to
  // the viewport. jsdom reports zero rects — the math degrades harmlessly.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  const anchorCenterX = rect ? rect.left + rect.width / 2 : vw / 2;
  const below = rect ? rect.top + rect.height / 2 < vh / 2 : true;
  const cardLeft = Math.max(EDGE, Math.min(anchorCenterX - CARD_WIDTH / 2, vw - CARD_WIDTH - EDGE));
  const cardTop = rect ? (below ? rect.bottom + SPOT_PAD + 14 : undefined) : vh / 2;
  const cardBottom = rect && !below ? vh - rect.top + SPOT_PAD + 14 : undefined;
  const caretLeft = Math.max(16, Math.min(anchorCenterX - cardLeft - 6, CARD_WIDTH - 28));

  // Portaled to <body>, not rendered where it is mounted. The whole mark lives in
  // one `position: fixed; inset: 0` layer, and a fixed box still honours its own
  // margin: mounted as the last child of the Trip Plan tab's `space-y-5`, it
  // inherited `margin-top: 20px` and every spotlight, card and caret drew 20px
  // below its target (JAR-1395). The first-run tour, mounted at the Dashboard
  // root, measured perfect — which is how this hid. At <body> no ancestor's
  // spacing, transform or overflow can re-frame the layer.
  return createPortal(
    <div className="fixed inset-0 z-[60]">
      {/* Tap-anywhere dismiss layer — the tour never traps the user. */}
      <div data-coachmark-scrim className="absolute inset-0" onClick={onDismiss} />

      {/* Spotlight: one transparent window over the anchor; the giant shadow
          is the dim. Visual only — clicks fall through to the dismiss layer.
          The 200ms glide plays only when the anchor CHANGES (step → step);
          same-anchor tracking (scroll/resize) applies instantly so the window
          never rubber-bands behind the user's scroll. */}
      {rect && (
        <div
          aria-hidden="true"
          className={`absolute rounded-xl pointer-events-none ${glide ? 'transition-all duration-200 ease-out' : ''}`}
          style={{
            left: rect.left - SPOT_PAD,
            top: rect.top - SPOT_PAD,
            width: rect.width + SPOT_PAD * 2,
            height: rect.height + SPOT_PAD * 2,
            boxShadow: '0 0 0 200vmax rgb(0 0 0 / 0.5)',
          }}
        />
      )}

      <div
        ref={cardRef}
        role="dialog"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="absolute w-[280px] rounded-2xl border border-border bg-surface-raised shadow-xl p-4 outline-none animate-reveal"
        style={{ left: cardLeft, top: cardTop, bottom: cardBottom }}
      >
        {/* Caret pointing at the anchor */}
        <div
          aria-hidden="true"
          className={`absolute w-3 h-3 rotate-45 bg-surface-raised border-border ${
            below ? '-top-1.5 border-l border-t' : '-bottom-1.5 border-r border-b'
          }`}
          style={{ left: caretLeft }}
        />

        <h2 id={titleId} className="text-sm font-semibold text-ink">{title}</h2>
        <p className="mt-1 text-sm text-body leading-snug">{body}</p>

        <div className="mt-3 flex items-center justify-between gap-2">
          {single ? (
            <span />
          ) : (
            <div className="flex items-center gap-1.5" aria-hidden="true">
              {Array.from({ length: totalSteps }, (_, i) => (
                <span
                  key={i}
                  className={`rounded-full transition-all duration-200 ${
                    i + 1 === step ? 'w-4 h-1.5 bg-brand' : 'w-1.5 h-1.5 bg-border'
                  }`}
                />
              ))}
            </div>
          )}
          {!single && <span className="sr-only">{`Step ${step} of ${totalSteps}`}</span>}

          <div className="flex items-center gap-1">
            {!single && !isLast && (
              <button
                onClick={onDismiss}
                className="px-2.5 py-1.5 text-sm font-medium text-muted hover:text-ink rounded-lg transition-colors"
              >
                Skip tour
              </button>
            )}
            <button
              onClick={onNext}
              className="px-3 py-1.5 text-sm font-semibold text-accent hover:bg-accent/10 rounded-lg transition-colors"
            >
              {nextLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
